// @ts-check
// ====== Accessibilità modali ======
// Aggiunge a ogni .modal: role=dialog, aria-modal, focus trap, ESC close,
// return-focus sull'elemento che ha aperto la modale.
//
// Espone openA11yModal(id, {labelledBy, returnFocus}) / closeA11yModal(id).
// Si aggancia ai modali esistenti senza cambiarne la logica interna:
// osserviamo l'attributo class per detect open/close e portarci dietro
// focus + listener tastiera.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/** @type {Map<string, {previousFocus: Element|null, keyHandler: (e: KeyboardEvent)=>void}>} */
const _state = new Map();

function getFocusable(root) {
  return /** @type {HTMLElement[]} */ ([...root.querySelectorAll(FOCUSABLE)]).filter(
    (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
  );
}

function trapFocus(modal, e) {
  if (e.key !== 'Tab') return;
  const focusables = getFocusable(modal);
  if (!focusables.length) {
    e.preventDefault();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function findCloseButton(modal) {
  return modal.querySelector('[data-close], [data-confirm-close], [data-close-ruolo]');
}

function setupModalA11y(modal) {
  if (!modal || modal.dataset.a11yReady === '1') return;
  modal.dataset.a11yReady = '1';
  modal.setAttribute('role', modal.getAttribute('role') || 'dialog');
  modal.setAttribute('aria-modal', 'true');

  // Se non c'è un labelledby, cerca h1/h2/h3 dentro la modale e usalo come label.
  if (!modal.getAttribute('aria-labelledby')) {
    const heading = modal.querySelector('h1,h2,h3');
    if (heading) {
      if (!heading.id) heading.id = (modal.id || 'modal') + 'Title';
      modal.setAttribute('aria-labelledby', heading.id);
    }
  }
}

function onModalOpen(modal) {
  setupModalA11y(modal);
  const id = modal.id;
  if (!id || _state.has(id)) return;
  const previousFocus = document.activeElement;
  const keyHandler = (e) => {
    if (e.key === 'Escape') {
      const closeBtn = /** @type {HTMLElement|null} */ (findCloseButton(modal));
      if (closeBtn) closeBtn.click();
      else modal.classList.add('hidden');
    } else if (e.key === 'Tab') {
      trapFocus(modal, e);
    }
  };
  document.addEventListener('keydown', keyHandler);
  _state.set(id, { previousFocus, keyHandler });

  // Focus iniziale: primo input non hidden, oppure primo focusable
  setTimeout(() => {
    const focusables = getFocusable(modal);
    const target =
      focusables.find((el) => el.matches('input:not([type="hidden"]), textarea, select')) ||
      focusables[0];
    if (target) target.focus();
  }, 30);
}

function onModalClose(modal) {
  const id = modal.id;
  const st = _state.get(id);
  if (!st) return;
  document.removeEventListener('keydown', st.keyHandler);
  _state.delete(id);
  const prev = /** @type {HTMLElement|null} */ (st.previousFocus);
  if (prev && typeof prev.focus === 'function') {
    try {
      prev.focus();
    } catch (_) {
      /* node detached */
    }
  }
}

// Osserva i .modal e reagisce al toggle della classe "hidden"
function initModalA11y() {
  document.querySelectorAll('.modal').forEach(
    /** @param {Element} el */ (el) => {
      const modal = /** @type {HTMLElement} */ (el);
      setupModalA11y(modal);
      if (!modal.classList.contains('hidden')) onModalOpen(modal);

      const mo = new MutationObserver((muts) => {
        muts.forEach((m) => {
          if (m.attributeName !== 'class') return;
          const hidden = modal.classList.contains('hidden');
          if (hidden) onModalClose(modal);
          else onModalOpen(modal);
        });
      });
      mo.observe(modal, { attributes: true, attributeFilter: ['class'] });
    }
  );
}

// Auto-init quando il DOM è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initModalA11y);
} else {
  initModalA11y();
}

// @ts-ignore — espongo per debug
globalThis.initModalA11y = initModalA11y;
export {};
